<template>
    <table
        class="Table"
        :class=" { Header: withHeader }"
    >
        <thead v-if="withHeader">
            <tr
                v-for="(headers, headerIndex) in columnHeadersList"
                :key="headerIndex"
                @click="$emit('onClickHead', { headers, headerIndex }, $event)"
            >
                <th
                    v-for="(column, columnIndex) in headers"
                    :key="columnIndex"
                    :colspan="column.colspan"
                    :class="[column.key, column.align, column.columnClass]"
                >
                    <div>
                        <slot
                            :name="`HEAD_${column.key}`"
                            :column="column"
                            :columnIndex="columnIndex"
                        >
                            {{ column.label }}
                        </slot>
                    </div>
                </th>
            </tr>
        </thead>
        <tbody>
            <tr
                v-for="(row, rowIndex) in actualRows"
                :key="row.id !== undefined ? `ROW_${row.id}` : `ROW_I_${rowIndex}`"
                :class="[row.rowClass, row.strippedClass]"
                @click="$emit('onClickRow', { row, rowIndex }, $event)"
            >
                <td
                    v-for="(column, columnIndex) in columnsForRow(row)"
                    :key="columnIndex"
                    :class="[column.key, column.align, column.cellClass, row.cellClass, getCellClass(column, row)]"
                    :colspan="column.cellColspan"
                    :style="{ width: column.width }"
                >
                    <div>
                        <slot
                            :name="`CELL_${column.key}`"
                            :column="column"
                            :columnIndex="columnIndex"
                            :row="row"
                            :rowIndex="rowIndex"
                            :cell="row[column.key]"
                        >
                            <slot
                                name="CELL"
                                :column="column"
                                :columnIndex="columnIndex"
                                :row="row"
                                :rowIndex="rowIndex"
                                :cell="row[column.key]"
                            >
                                {{ row[column.key] }}
                            </slot>
                        </slot>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import VueTypes from 'vue-types'

const columnHeaderVueType = VueTypes.arrayOf(VueTypes.shape({
    label: VueTypes.string,
    colspan: VueTypes.number,
    align: VueTypes.string,
    columnClass: VueTypes.string,
}).loose)

export default {
    name: 'Table',
    props: {
        // Prop for having two headers. This prop is for the top header-columns
        columnHeaders: VueTypes.oneOfType([columnHeaderVueType, VueTypes.arrayOf(columnHeaderVueType)]).optional,
        columns: VueTypes.arrayOf(VueTypes.shape({
            key: VueTypes.string,
            label: VueTypes.string,
            align: VueTypes.string,
            width: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
            columnClass: VueTypes.string,
            cellClass: VueTypes.string,
        }).loose).isRequired,
        rows: VueTypes.arrayOf(VueTypes.shape({
            id: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
            rowClass: VueTypes.string,
            cellClass: VueTypes.string,
        }).loose).isRequired,
        cellClass: VueTypes.func.optional,
        withHeader: VueTypes.bool.def(true),
        stripped: VueTypes.bool.def(false),
        mergeColumnsForUndefinedValues: VueTypes.bool.def(false),
    },
    computed: {
        columnHeadersList () {
            const { columnHeaders, columns } = this
            if (Array.isArray(columnHeaders)) {
                return Array.isArray(columnHeaders[0]) ? columnHeaders : [columnHeaders]
            }
            return [columns]
        },
        actualRows () {
            const { rows } = this
            if (this.stripped) {
                return rows.map((row, index) => ({ ...row, strippedClass: index % 2 === 0 ? 'odd' : 'even' }))
            }
            return rows
        },
        getCellClass () {
            const { cellClass } = this
            return (column, row) => (cellClass ? cellClass(column, row) : undefined)
        },
        columnsForRow () {
            const { columns: allColumns, mergeColumnsForUndefinedValues } = this
            if (mergeColumnsForUndefinedValues) {
                return (row) => {
                    const columns = []
                    let i = 0
                    while (i < allColumns.length) {
                        let colspan = 1
                        while (i + colspan < allColumns.length && row[allColumns[i + colspan].key] === undefined) {
                            colspan += 1
                        }
                        columns.push(colspan === 1 ? allColumns[i] : { ...allColumns[i], cellColspan: colspan })
                        i += colspan
                    }
                    return columns
                }
            }
            return () => allColumns
        },
    },
}
</script>

<style lang="scss" scoped>
// Customer classes
.customClasses {
    // Weights
    &.bold {
        font-weight: bold;
    }

    // ALigns
    &.center {
        text-align: center;
    }
    &.right {
        text-align: right;
    }
}

.Table {
	width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    font-family: $font-family-table;
}

// HEADER
thead {
    text-transform: uppercase;

	tr {
		border-bottom: 1px solid $gray-05;
		// border bottom if there is a second header
		& + tr {
			border-bottom: 2px solid $gray-05;
		}
    }

	th {
		padding: 15px;
	}
}

th, td {
    text-align: left;
    @extend .customClasses;

	// If you have in columns align and you want to change alignment to the column but the header for example
	&.center-important {
		text-align: center !important;
	}
	&.right-important {
		text-align: right !important;
	}
}

// Class added with the columnClass prop (columns, columnHeaders)
th.title {
	text-transform: none;

	& > div {
		margin-top: -5px;
	}
}

// TBODY
tbody tr {
    border-bottom: 1px solid $gray-05;
    // Class added with the rowClass prop (rows)
    @extend .customClasses;

	td {
        padding: 15px;
		// Classes added with the cellClass prop (columns, rows)
        @extend .customClasses;
    }

    &:last-child td {
		border-bottom: 1px solid $gray-05;
	}

	// Style for the zebraClass
	&.odd td {
		background: $white;
	}
	&.odd + .odd {
		td + td {
			border-top: 1px solid $gray-05;
		}
	}
	&.even td {
		background: $gray-05;
	}
	&.even + .even {
		td + td {
			border-top: 1px solid $white;
		}
	}
}

</style>
